/**
 * Route paths that logic — not just the router — has to reason about, plus a
 * comparison that answers "would the router render this route for this path?".
 *
 * Getting this wrong desynchronizes logic from what is on screen. React Router
 * matches case-insensitively, ignores trailing slashes, AND percent-decodes each
 * path segment, so `/welcome/`, `/WELCOME` and `/wel%63ome` all render the welcome
 * screen. Comparing `pathname === '/welcome'` missed every one of those: a settled
 * non-English visitor at such a path got the English welcome screen while
 * `<html lang>` stayed on their locale (selecting a multi-megabyte CJK webfont for
 * a screen that renders none of it), and a first-time visitor sent to `/welcome`
 * from an alias was returned to that alias on confirm — landing back on the welcome
 * screen instead of entering the app.
 *
 * Rather than re-implement those rules (every hand-rolled attempt got an edge case
 * wrong — naive decoding wrongly matches `/welcome%2F`, which the router keeps
 * encoded and does NOT match), `isRoute` delegates to the very function the router
 * uses. `<Routes>` resolves through `matchRoutes`, not `matchPath`: only the former
 * decodes, so it is the one correct oracle here.
 *
 * Keep every such comparison going through `isRoute` so the two can never drift.
 */

import { matchRoutes } from 'react-router-dom';

export const ROUTE_WELCOME = '/welcome';
/** Post-welcome onboarding: create a wallet with a passkey. Pre-auth, so it sits
 *  outside RequireAuth — distinct from `/create`, which is in-app wallet creation. */
export const ROUTE_START = '/start';
export const ROUTE_LOGIN = '/login';
/**
 * In-app wallet creation: the manual form, one chain at a time. Distinct from
 * {@link ROUTE_PROVISIONING}, which creates the whole standard set
 * automatically.
 *
 * This was a bare `'/create'` literal in `App.tsx` while every sibling route had
 * a constant. It became a second call site when provisioning gained a "create
 * manually" escape, and two literals for one path is how they drift apart.
 */
export const ROUTE_CREATE = '/create';
/**
 * Language and appearance, the one settings surface that works without an account.
 *
 * It sits under /settings by name because that is where it belongs conceptually, but it
 * is a SEPARATE, PUBLIC route — /settings itself is behind RequireAuth, and routes here
 * are flat, so nothing is inherited. A signed-out visitor must be able to reach it: the
 * welcome screen offers the language picker once, and every other switcher is gated, so
 * without this a visitor who picked the wrong language has no way back.
 */
export const ROUTE_LANGUAGE_APPEARANCE = '/settings/language-appearance';
/* The other menu destinations. These are authenticated; the language page above is
   deliberately not (see its own file). There is no bare /settings any more — the
   menu drawer is the hub, so the old hub page has no rows left to list. */
export const ROUTE_SECURITY = '/settings/security';
export const ROUTE_DEVELOPER = '/settings/developer';
export const ROUTE_OAUTH_CALLBACK = '/oauth/callback';
export const ROUTE_SWAP = '/swap';
/**
 * The last step of registration: the account's standard wallets being created.
 *
 * A ROUTE rather than a state inside the signup view, and deliberately so. The
 * ceremonies run server-side and take real time, so a refresh, an accidental
 * back, or a browser restart mid-provisioning is an ordinary thing to happen. As
 * a route the screen simply reloads and picks the work back up; as view state it
 * would drop the user onto an empty dashboard with no explanation and no way
 * back to the progress they were watching.
 *
 * It is AUTHED — provisioning belongs to an account, and by the time a user
 * reaches it signup has already minted a session.
 */
export const ROUTE_PROVISIONING = '/welcome/wallets';

/**
 * Whether `path` addresses `route`.
 *
 * Scoped to the static, top-level `ROUTE_*` paths above, where matching one pattern in
 * isolation gives the same answer as the full route table. It is NOT a general router
 * predicate: a pattern with a dynamic segment or splat can match here while the real
 * table picks a more specific competing route, a nested/relative path needs its parent
 * context, a `caseSensitive` route declaration is not represented, and the pre-render
 * caller in main.tsx passes `window.location.pathname`, which would still include a
 * basename if one were ever configured (today `<BrowserRouter>` has none).
 *
 * `path` may carry a query — navigation state stores `pathname + search` — which
 * matchRoutes parses itself. Malformed escapes (`/%`) return false rather than throw.
 */
export function isRoute(path: string, route: string): boolean {
  // React Router catches malformed percent escapes and returns no match, but it also
  // logs a warning while doing so. Validate only the pathname before handing the
  // original value to the router: decoding here is a syntax check, not our matcher,
  // so encoded separators such as `%2F` keep React Router's exact semantics.
  const hashIndex = path.indexOf('#');
  const withoutHash = hashIndex === -1 ? path : path.slice(0, hashIndex);
  const searchIndex = withoutHash.indexOf('?');
  const pathname = searchIndex === -1 ? withoutHash : withoutHash.slice(0, searchIndex);

  try {
    decodeURIComponent(pathname);
  } catch {
    return false;
  }

  return matchRoutes([{ path: route }], path) !== null;
}

/**
 * Where the welcome screen sends the visitor once they confirm their language.
 *
 * `from` is what WelcomeGate recorded when it intercepted the visit — `pathname +
 * search` of wherever they were headed — or undefined if nothing was intercepted.
 * The rule has to separate a REAL destination from the mere absence of one:
 *
 *   - undefined      nothing was intercepted (they opened /welcome directly)
 *   - "/"            they just opened the app; the root is not a destination
 *   - the welcome    an alias of this very screen; honouring it would loop
 *   - anything else  a genuine deep link, and it must survive the language choice
 *
 * The `"/"` case is the subtle one and was originally missed. WelcomeGate records the
 * root for an ordinary first visit — by far the commonest entry — so treating any
 * non-welcome `from` as a destination sent every new user to `/`, which is behind
 * RequireAuth, which bounced them to `/login`. The create-wallet screen was reachable
 * only by opening `/welcome` or `/start` directly, so a brand-new user was shown
 * "sign in" instead of "create a wallet".
 *
 * Comparing `"/"` exactly (not via isRoute) is deliberate: `from` carries the query
 * string, so a real deep link such as `/?org=<id>` is NOT equal to `"/"` and is
 * correctly preserved. isRoute would match both and discard the invite.
 */
export function resolveWelcomeDestination(from: string | undefined): string {
  if (!from || from === '/' || isRoute(from, ROUTE_WELCOME)) return ROUTE_START;
  return from;
}
